import type { FleetGateway } from '../../core/bridge/connect-client';
import type { ControllerManager } from './controller-manager';
import type { CameraManager } from './camera-manager';
import type { ModelManager } from './model-manager';
import type { IntegrationManager } from './integration-manager';
import type { DashboardManager } from './dashboard-manager';
import type { TransportManager } from './transport-manager';
import type { PluginTypesManager } from './plugin-types-manager';
import type { ConnectionManager } from './connection-manager';

/**
 * One org gateway as the shell renders it: its fleet roster entry (always
 * present — the card + tree node show even when nothing's connected) bundled
 * with its three live, per-gateway managers (each scoped to `gateway.
 * deviceFingerprint`, racing/reconnecting independently).
 *
 * The shell owns the managers' lifecycle + subscribes to their `change` events
 * (re-rendering itself), so `vitesse-home` / `vitesse-tree` consume an
 * `GatewayEntry[]` as PURE renderers — they read `entry.controllers.all` etc.
 * at render time and never wire their own listeners. Multi-gateway is just
 * "iterate the entries."
 */
export interface GatewayEntry {
  gateway: FleetGateway;
  controllers: ControllerManager;
  /** The camera ROSTER for this gateway (descriptors only; the live media +
   *  its look-away/reconnect lifecycle live in the main-thread
   *  CameraStreamManager, opened on demand per viewed feed). */
  cameras: CameraManager;
  models: ModelManager;
  /** The 2nd integration API roster (DBs/brokers/REST/OT modeled as PLC-like
   *  read/write-by-path entities) for this gateway. */
  integrations: IntegrationManager;
  /** The dashboard ROSTER for this gateway - summaries only; a body is fetched
   *  when one is opened, because a dashboard carrying widget source is large. */
  dashboards: DashboardManager;
  transport: TransportManager;
  /** The plugin types this gateway advertises (drives the backend-driven
   *  "Add controller" picker + per-type connection forms). */
  pluginTypes: PluginTypesManager;
  /** The frontend's connect/disconnect INTENT for this gateway (the
   *  Connect/Disconnect/Cancel control) — frontend-only, never touches the box. */
  connection: ConnectionManager;
}

/**
 * The human label for a gateway — user label, else hostname, else the device
 * fingerprint. Shared by the tree node AND the gateway-detail header so they
 * always show the SAME identifier (no friendly-name-in-the-tree /
 * raw-fingerprint-in-the-header mismatch).
 */
export function gatewayLabel(g: FleetGateway): string {
  return g.displayName || g.hostname || g.deviceFingerprint;
}
